import { useEffect, useRef, useState } from "react";
import { fetchPartHistoryQuotes } from "../../models/shared/partHistoryModel";

// parts: [{ key, partId, partName }] — at least one of partId/partName
// per entry; entries with neither are dropped before the request goes out.
//
// debounceMs: New Query form passes ~500 since parts are live-typed;
// Manage Price passes 0 (default) since its parts list is static once the
// drawer is open, nothing to debounce against.
export function usePartHistoryQuotes({ makeId, modelId, parts = [], year, countrySpec, excludeQueryPk, debounceMs = 0 }) {
  const [loading, setLoading] = useState(false);
  const [resultsByKey, setResultsByKey] = useState({});
  const debounceRef = useRef(null);

  const usableParts = parts.filter(p => p.canonicalPartId != null || p.partId != null || (p.partName && p.partName.trim()));
  // Stable string key so a `parts` array that's re-created every render
  // (a new array literal with the same contents) doesn't retrigger the
  // fetch — only an actual change to which parts/ids/names are requested
  // should.
  const partsKey = usableParts
    .map(p => `${p.key}:${p.canonicalPartId ?? ""}:${p.partId ?? ""}:${(p.partName || "").trim().toLowerCase()}`)
    .join("|");

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!makeId || !partsKey) {
      setResultsByKey({});
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      const data = await fetchPartHistoryQuotes({
        makeId, modelId, year, countrySpec, excludeQueryPk,
        parts: usableParts.map(p => ({
          key: p.key,
          ...(p.canonicalPartId != null
            ? { uzdpart360_part_id: p.canonicalPartId }
            : { part_id: p.partId ?? null, part_name: p.partId ? null : p.partName }),
        })),
      });
      setResultsByKey(data || {});
      setLoading(false);
    }, debounceMs);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [makeId, modelId, partsKey, year, countrySpec, excludeQueryPk, debounceMs]);

  return { loading, resultsByKey };
}
